import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MailX, Lock, Plus, Trash2, CircleAlert } from 'lucide-react';
import { useAccessToken } from '@/auth/useAccessToken';
import { useMe } from '@/features/me/useMe';
import { useIgnoredSenders } from '@/features/settings/useIgnoredSenders';
import { ignoredSendersApi } from '@/features/settings/ignoredSendersApi';
import { ApiError } from '@/lib/apiClient';

/**
 * /settings/ignored-senders — IT-Admin management of the inbound ignore-list.
 * Mail from these senders never opens or appends to a ticket. Patterns: an
 * exact address, a whole domain (@bulk.example.com), or a glob (svc-*@aesintl.com).
 * Common no-reply/daemon addresses are filtered in code regardless.
 */
export function AdminIgnoredSenders() {
  const me = useMe();
  const isAdmin = me.data?.role === 'ITAdmin';
  const senders = useIgnoredSenders();
  const forbidden = (senders.error as ApiError)?.status === 403 || (me.data && !isAdmin);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <MailX className="w-6 h-6 text-primary" /> Ignored senders
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Mail from these senders never opens or appends to a ticket — for alert robots, report jobs, and
          mailing lists. Use an exact address, a whole domain (<code>@bulk.example.com</code>), or a glob
          (<code>svc-*@aesintl.com</code>). Common no-reply addresses are blocked automatically.
        </p>
      </header>

      {forbidden && (
        <div className="rounded-xl border border-dashed bg-card p-10 text-center">
          <Lock className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
          <div className="text-base font-medium">Admin access required</div>
          <p className="text-sm text-muted-foreground mt-1.5">Only IT Admins can manage ignored senders.</p>
        </div>
      )}

      {!forbidden && (
        <>
          <AddSender />
          {senders.isPending && <p className="text-sm text-muted-foreground mt-4">Loading…</p>}
          {senders.isError && !forbidden && (
            <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-start gap-2">
              <CircleAlert className="w-4 h-4 mt-0.5" />
              <span>Couldn't load ignored senders: {(senders.error as Error).message}</span>
            </div>
          )}
          {senders.data && <SenderTable />}
        </>
      )}
    </div>
  );
}

function AddSender() {
  const getAccessToken = useAccessToken();
  const queryClient = useQueryClient();
  const [pattern, setPattern] = useState('');
  const [note, setNote] = useState('');

  const create = useMutation({
    mutationFn: () => ignoredSendersApi.create(pattern.trim(), note.trim() || null, getAccessToken),
    onSuccess: () => {
      setPattern('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['ignored-senders'] });
    }
  });

  const err = create.error as ApiError | undefined;
  const message =
    err?.status === 409
      ? 'That sender is already on the list.'
      : err?.status === 400
        ? 'Use an email, a domain (@example.com), or a glob (svc-*@example.com).'
        : err
          ? 'Could not add the sender.'
          : null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (pattern.trim()) create.mutate();
      }}
      className="rounded-xl border bg-card p-4 flex flex-col sm:flex-row items-start gap-2"
    >
      <input
        value={pattern}
        onChange={(e) => setPattern(e.target.value)}
        maxLength={200}
        placeholder="robot@aesintl.com"
        className="flex-1 w-full bg-background border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 ring-primary/40"
      />
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={200}
        placeholder="Note (optional)"
        className="flex-1 w-full bg-background border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 ring-primary/40"
      />
      <button
        type="submit"
        disabled={!pattern.trim() || create.isPending}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-60"
      >
        <Plus className="w-4 h-4" /> Add
      </button>
      {message && <p className="w-full text-xs text-destructive">{message}</p>}
    </form>
  );
}

function SenderTable() {
  const senders = useIgnoredSenders();
  const items = senders.data ?? [];

  if (items.length === 0) {
    return (
      <div className="mt-4 rounded-xl border border-dashed bg-card p-10 text-center text-sm text-muted-foreground">
        No ignored senders yet — add one above.
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border bg-card divide-y">
      {items.map((s) => (
        <SenderRow key={s.id} id={s.id} pattern={s.pattern} note={s.note} />
      ))}
    </div>
  );
}

function SenderRow({ id, pattern, note }: { id: string; pattern: string; note: string | null }) {
  const getAccessToken = useAccessToken();
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: () => ignoredSendersApi.remove(id, getAccessToken),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['ignored-senders'] })
  });

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-mono truncate">{pattern}</div>
        {note && <div className="text-xs text-muted-foreground truncate">{note}</div>}
      </div>
      <button
        type="button"
        disabled={remove.isPending}
        onClick={() => {
          if (confirm(`Remove “${pattern}” from the ignore-list?`)) remove.mutate();
        }}
        className="text-muted-foreground hover:text-destructive disabled:opacity-40"
        aria-label="Remove"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
